import * as Sentry from "@sentry/react";
import isEmpty from "lodash/isEmpty";
import {UseFormSetError} from "react-hook-form";
import {toast} from "react-hot-toast";
import * as Yup from "yup";

import {ServerErrors} from "@/lib/types/common.ts";
import {CredentialSecret, CredentialType} from "@/lib/types/credentials.ts";
import {UpdateCredentialFormValues} from "@/pages/vault/vaultForm/CredentialUpdateForm.tsx";

export const YupCredentialSecretSchema = Yup.object().when("credential_type", (values, schema) => {
  const credential_type = values[0];
  if (credential_type === CredentialType.BASIC) {
    return schema.shape({
      password: Yup.string().required("Password is required"),
    });
  }
  if (credential_type === CredentialType.TOKEN) {
    return schema.shape({
      secret: Yup.string().required("Token is required"),
    });
  }
  if (credential_type === CredentialType.CERTIFICATE) {
    return schema.shape(
      {
        certificate: Yup.string()
          .transform((v, orig) => (orig === "" ? undefined : v))
          .when("certificate_file", (certificate_file, schema) => {
            if (!isEmpty(certificate_file) && certificate_file[0]) {
              return schema;
            }
            return schema.required("One certificate field is required");
          })
          .test(
            "onlyOneCertificateField",
            "Please only fill out one certificate field",
            (value, testContext: Yup.TestContext<CredentialSecret>) => {
              if (value) {
                return !testContext.parent.certificate_file;
              }
              return true;
            }
          ),
        certificate_file: Yup.mixed()
          .transform((v) => {
            if (v && v.length > 0) {
              return v;
            }
            return undefined;
          })
          .when("certificate", (certificate, schema) => {
            if (!isEmpty(certificate) && certificate[0]) {
              return schema;
            }
            return schema.required("One certificate field is required");
          })
          .test(
            "onlyOneCertificateField",
            "Please only fill out one certificate field",
            (value, testContext: Yup.TestContext<CredentialSecret>) => {
              if (value) {
                return !testContext.parent.certificate;
              }
              return true;
            }
          ),
        key_file: Yup.mixed()
          .transform((v) => {
            if (v && v.length > 0) {
              return v;
            }
            return undefined;
          })
          .optional(),
        passphrase: Yup.string()
          .transform((v, orig) => (orig === "" ? undefined : v))
          .optional(),
      },
      [["certificate", "certificate_file"]]
    );
  }
  if (credential_type === CredentialType.TOTP) {
    return schema.shape({
      secret: Yup.string().required("Secret is required"),
      period: Yup.number()
        .transform((v, orig) => (orig === "" ? undefined : v))
        .typeError("Must be a number")
        .required("Period is required"),
      num_digits: Yup.number()
        .transform((v, orig) => (orig === "" ? undefined : v))
        .typeError("Must be a number")
        .required("Number of digits is required"),
    });
  }
  return schema;
});

export const YupCredentialUpdateSecretsSchema = (credentialType: CredentialType) => {
  if (credentialType === CredentialType.BASIC) {
    return Yup.object().shape({
      password: Yup.string(),
    });
  }
  if (credentialType === CredentialType.TOKEN) {
    return Yup.object().shape({
      secret: Yup.string(),
    });
  }
  if (credentialType === CredentialType.CERTIFICATE) {
    return Yup.object().shape({
      certificate: Yup.string()
        .transform((v, orig) => (orig === "" ? undefined : v))
        .test(
          "certificateRequired",
          "One certificate field is required",
          (value, testContext: Yup.TestContext<CredentialSecret>) => {
            if (value) {
              return true;
            }
            return !(
              (testContext.parent.key_file || testContext.parent.passphrase) &&
              !testContext.parent.certificate_file
            );
          }
        )
        .test(
          "onlyOneCertificateField",
          "Please only fill out one certificate field",
          (value, testContext: Yup.TestContext<CredentialSecret>) => {
            if (value) {
              return !testContext.parent.certificate_file;
            }
            return true;
          }
        ),
      certificate_file: Yup.mixed()
        .transform((v) => {
          if (v && v.length > 0) {
            return v;
          }
          return undefined;
        })
        .test(
          "certificateFileRequired",
          "One certificate field is required",
          (value, testContext: Yup.TestContext<CredentialSecret>) => {
            if (value) {
              return true;
            }
            return !((testContext.parent.key_file || testContext.parent.passphrase) && !testContext.parent.certificate);
          }
        )
        .test(
          "onlyOneCertificateField",
          "Please only fill out one certificate field",
          (value, testContext: Yup.TestContext<CredentialSecret>) => {
            if (value) {
              return !testContext.parent.certificate;
            }
            return true;
          }
        ),
      key_file: Yup.mixed()
        .transform((v) => {
          if (v && v.length > 0) {
            return v;
          }
          return undefined;
        })
        .optional(),
      passphrase: Yup.string().transform((v, orig) => (orig === "" ? undefined : v)),
    });
  }
  if (credentialType === CredentialType.TOTP) {
    return Yup.object().shape({
      secret: Yup.string().test(
        "updateSecretRequired",
        "Secret is required",
        (value, testContext: Yup.TestContext<CredentialSecret>) => {
          if (value) {
            return true;
          }
          return !(testContext.parent.period || testContext.parent.num_digits);
        }
      ),
      period: Yup.number()
        .transform((v, orig) => (orig === "" ? undefined : v))
        .typeError("Must be a number")
        .test("updatePeriodRequired", "Period is required", (value, testContext: Yup.TestContext<CredentialSecret>) => {
          if (value) {
            return true;
          }
          return !(testContext.parent.secret || testContext.parent.num_digits);
        }),
      num_digits: Yup.number()
        .transform((v, orig) => (orig === "" ? undefined : v))
        .typeError("Must be a number")
        .test(
          "updateNumDigitsRequired",
          "Number of digits is required",
          (value, testContext: Yup.TestContext<CredentialSecret>) => {
            if (value) {
              return true;
            }
            return !(testContext.parent.secret || testContext.parent.period);
          }
        ),
    });
  }
  return Yup.object().shape({});
};

const nestedErrorIndex = (index: string) => {
  if (index === "") {
    return "";
  }
  return index + ".";
};

export const setServerErrors = (
  serverErrors: ServerErrors,
  setError: UseFormSetError<never>,
  index: string,
  fieldsInForm?: string[] // Ideally, we would pass in the registered form fields but that doesn't seem possible with react-hook-form right now
) => {
  const errorsNotInForm: string[] = [];
  if (serverErrors && typeof serverErrors === "object") {
    Object.keys(serverErrors).forEach((errorField) => {
      if (errorField === "__all__") {
        return;
      }
      if (errorField === "non_field_errors") {
        return;
      }
      if (serverErrors[errorField]) {
        if (Array.isArray(serverErrors[errorField])) {
          const errorFieldName = nestedErrorIndex(index) + errorField;
          if (fieldsInForm && !fieldsInForm.includes(errorFieldName)) {
            errorsNotInForm.push(`${errorField}: ${serverErrors[errorField][0]}`);
            Sentry.captureException(new Error("Form field error not in form"), {
              extra: {
                errorFieldName,
                errorMessage: serverErrors[errorField][0],
              },
            });
          }
          // @ts-expect-error argument type mismatch
          setError(errorFieldName, {message: serverErrors[errorField][0]});
        } else {
          const nestedErrors = setServerErrors(
            serverErrors[errorField],
            setError,
            nestedErrorIndex(index) + errorField,
            fieldsInForm
          );
          errorsNotInForm.push(...nestedErrors);
        }
      }
    });
  }
  return errorsNotInForm;
};

export const file2String = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => resolve(reader.result?.toString() || "");
    reader.onerror = (error) => reject(error);
  });
};

export const handleFormatingCertificateFiles = async (data: UpdateCredentialFormValues) => {
  if (data.secret && data.secret.certificate_file) {
    try {
      data.secret.certificate = await file2String(data.secret.certificate_file[0]);
      delete data.secret.certificate_file;
    } catch (error) {
      Sentry.captureException(error, {data: "Failed to read certificate file"});
      toast.error("Failed to read certificate file");
    }
  }
  if (data.secret && data.secret.key_file) {
    try {
      data.secret.key = await file2String(data.secret.key_file[0]);
      delete data.secret.key_file;
    } catch (error) {
      Sentry.captureException(error, {data: "Failed to read certificate file"});
      toast.error("Failed to read key file");
    }
  }
};
